
import Vue, { PropOptions } from 'vue';
import _ from 'lodash';
import { numFormat } from '@/helpers/numFormat';
import { SalesFunnel } from '@/models/page-models/page-items/SalesFunnel';

export default Vue.extend({
  props: {
    input: { type: Object, default: () => {} } as PropOptions<SalesFunnel>,
    data: { type: Object, default: () => {} },
    getVariable: { type: Function, default: () => () => {} },
    callFunction: { type: Function, default: () => () => {} },
    onChange: { type: Function, default: () => {} },
    parent: { type: Object, default: () => {} },
    rowDisabled: { type: Boolean, default: false }
  },
  data() {
    return { 
      salesGroupExpanded: '', 
      salesmanExpanded: '' 
    };
  },
  computed: {
    tableHeaders: function(): { text: string, value: string, align?: string }[] {
      const tableHeaders: { text: string, value: string, align?: string }[] = [
        { text: 'Group', value: 'salesGroup', align: 'center' },
        { text: 'Salesman', value: 'salesman', align: 'center' },
        { text: 'Customer', value: 'customerName', align: 'center' },
        { text: 'Type', value: 'customerType', align: 'center' },
        { text: 'Quotation', value: 'quotationId', align: 'center' }
      ];

      if (this.input.funnelType == 'Open') {
        tableHeaders.push(
          { text: 'Open QT', value: 'openQtValue', align: 'center' },
          { text: 'Pending QT', value: 'pendingQtValue', align: 'center' }
        );
      }

      if (this.input.funnelType == 'Closed') {
        tableHeaders.push(
          { text: 'Successful QT', value: 'successfulQtValue', align: 'center' },
          { text: 'New Items', value: 'newItemValue', align: 'center' },
          { text: 'Failed QT', value: 'failedQtValue', align: 'center' },
          { text: 'Reason', value: 'reason', align: 'center' }
        );
      }
      tableHeaders.push({ text: '', value: 'data-expand', align: 'end' });
      return tableHeaders;
    },
    salesFunnelData: function(): any {
      return this.getVariable(this.input.salesFunnelVariable);
    },
    groupedFunnelData: function(): any[] {
      // 1. Iterate over Sales Groups
      const funnelDataGroupedBySalesGroup = _.map(this.salesFunnelData, ((salesGroupFunnelData: any, salesGroup: string) => {
        
        // 2. Iterate over Salesmen
        const funnelDataGroupedBySalesman = _.map(salesGroupFunnelData, ((salesmanFunnelDataArray: any[], salesman: string) => {
          
          // Map individual quotations
          const salesmanQuotations = salesmanFunnelDataArray.map((quotation: any) => {
            let reason = '-';
            if (quotation.rejectionReason) {
              reason = quotation.rejectionReason == 'Other' 
                ? `Other - ${quotation.otherRejectionReason}` 
                : quotation.rejectionReason;
            }
            // Normalize fields from different APIs
            return {
              ...quotation,
              salesGroup,
              salesman,
              reason
            };
          });

          // Calculate Totals
          const openQtValue = salesmanQuotations.reduce((sum, q) => sum + (q.openQtValue || 0), 0);
          const pendingQtValue = salesmanQuotations.reduce((sum, q) => sum + (q.pendingQtValue || 0), 0);
          const successfulQtValue = salesmanQuotations.reduce((sum, q) => sum + (q.successfulQtValue || 0), 0);
          const failedQtValue = salesmanQuotations.reduce((sum, q) => sum + (q.failedQtValue || 0), 0);
          const newItemValue = salesmanQuotations.reduce((sum, q) => sum + (q.newItemValue || 0), 0);

          // Salesman Summary Row
          return {
            salesGroup,
            salesman,
            customerName: 'All',
            customerType: '-',
            quotationId: '-',
            openQtValue,
            pendingQtValue,
            successfulQtValue,
            failedQtValue,
            newItemValue,
            reason: '-',
            expandable: 'salesman',
            salesmanQuotations
          };
        }));

        // Group Total from Salesman Data
        const openQtValue = funnelDataGroupedBySalesman.reduce((sum, s) => sum + s.openQtValue, 0);
        const pendingQtValue = funnelDataGroupedBySalesman.reduce((sum, s) => sum + s.pendingQtValue, 0);
        const successfulQtValue = funnelDataGroupedBySalesman.reduce((sum, s) => sum + s.successfulQtValue, 0);
        const failedQtValue = funnelDataGroupedBySalesman.reduce((sum, s) => sum + s.failedQtValue, 0);
        const newItemValue = funnelDataGroupedBySalesman.reduce((sum, s) => sum + s.newItemValue, 0);

        // Group Summary Row
        return {
          salesGroup,
          salesman: 'All',
          customerName: 'All',
          customerType: '-',
          quotationId: '-',
          openQtValue,
          pendingQtValue,
          successfulQtValue,
          failedQtValue,
          newItemValue,
          funnelDataGroupedBySalesman,
          reason: '-',
          expandable: 'salesGroup'
        };
      }));

      // Grand Total
      const openQtValue = funnelDataGroupedBySalesGroup.reduce((sum, g) => sum + g.openQtValue, 0);
      const pendingQtValue = funnelDataGroupedBySalesGroup.reduce((sum, g) => sum + g.pendingQtValue, 0);
      const successfulQtValue = funnelDataGroupedBySalesGroup.reduce((sum, g) => sum + g.successfulQtValue, 0);
      const failedQtValue = funnelDataGroupedBySalesGroup.reduce((sum, g) => sum + g.failedQtValue, 0);
      const newItemValue = funnelDataGroupedBySalesGroup.reduce((sum, g) => sum + g.newItemValue, 0);

      const totalFunnelData: any[] = [
        {
          salesGroup: 'All',
          salesman: 'All',
          customerName: 'All',
          customerType: '-',
          quotationId: '-',
          openQtValue,
          pendingQtValue,
          successfulQtValue,
          failedQtValue,
          newItemValue,
          reason: '-'
        }
      ];

      // Flatten Data for Table Display (Group -> Salesman -> Quotations)
      _.sortBy(funnelDataGroupedBySalesGroup, 'salesGroup').forEach((salesGroupFunnelData: any) => {
        const { funnelDataGroupedBySalesman, ...salesGroupFunnelSummary } = salesGroupFunnelData;
        
        // Push Group Row
        totalFunnelData.push(salesGroupFunnelSummary);

        // If Group Expanded, process Salesmen
        if (salesGroupFunnelSummary.salesGroup == this.salesGroupExpanded) {
          _.sortBy(funnelDataGroupedBySalesman, 'salesman').forEach((salesmanFunnelData: any) => {
            const { salesmanQuotations, ...salesmanFunnelSummary } = salesmanFunnelData;
            
            // Push Salesman Row
            totalFunnelData.push(salesmanFunnelSummary);

            if (salesmanFunnelSummary.salesman == this.salesmanExpanded) {
              // sort by quotationId (Newest first)
              const sortedQuotations = _.orderBy(salesmanQuotations, ['quotationId'], ['desc']);  
              totalFunnelData.push(...sortedQuotations);
            }
          });
        }
      });

      return totalFunnelData;
    }
  },
  mounted() {
  },
  methods: {
    expand(expandableType: 'salesGroup' | 'salesman', expandValue: string) {
      if (expandableType == 'salesGroup') {
        this.salesGroupExpanded = expandValue;
      } else if (expandableType == 'salesman') {
        this.salesmanExpanded = expandValue;
      }
    },
    collapse(expandableType: 'salesGroup' | 'salesman') {
      if (expandableType == 'salesGroup') {
        this.salesGroupExpanded = '';
      }
      if (['salesGroup', 'salesman'].includes(expandableType)) {
        this.salesmanExpanded = '';
      }
    },
    formatNumber(numValue: number): string | number {
      return numFormat(numValue);
    }
  }
});
